import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { Structure } from '../types';
import { BrandPanel } from './Login';
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';

function getStrength(pwd: string): { level: number; label: string; color: string } {
  if (pwd.length === 0) return { level: 0, label: '', color: '' };
  if (pwd.length < 8)  return { level: 1, label: 'Trop court', color: 'bg-red-400' };
  if (pwd.length < 10) return { level: 2, label: 'Faible',     color: 'bg-orange-400' };
  if (pwd.length < 12) return { level: 3, label: 'Acceptable', color: 'bg-yellow-400' };
  return { level: 4, label: 'Fort', color: 'bg-green-500' };
}

const Register: React.FC = () => {
  /* ── Champs identité ── */
  const [prenom, setPrenom]         = useState('');
  const [nom, setNom]               = useState('');
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  /* ── Structure ── */
  const [structures, setStructures]       = useState<Structure[]>([]);
  const [structureSearch, setStructureSearch] = useState('');
  const [structureId, setStructureId]     = useState<string>('');
  const [newStructureNom, setNewStructureNom] = useState('');
  const [isNewStructure, setIsNewStructure]   = useState(false);
  const [showDropdown, setShowDropdown]   = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /* ── Mot de passe ── */
  const [showPwd, setShowPwd]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  /* ── État ── */
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [error, setError]         = useState('');

  const strength = getStrength(password);

  useEffect(() => {
    apiService.getStructures().then(setStructures).catch(() => {});
  }, []);

  /* Fermer le dropdown au clic extérieur */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Filtrage des structures ── */
  const filteredStructures = structures.filter((s) => {
    const q = structureSearch.toLowerCase();
    return s.nom.toLowerCase().includes(q) || (s.sigle || '').toLowerCase().includes(q);
  });

  const showCreateOption =
    structureSearch.trim().length >= 2 &&
    !structures.some((s) => s.nom.toLowerCase() === structureSearch.trim().toLowerCase());

  /* ── Sélectionner une structure existante ── */
  const handleSelectExisting = (s: Structure) => {
    setStructureId(s.id);
    setNewStructureNom('');
    setIsNewStructure(false);
    setStructureSearch('');
    setShowDropdown(false);
  };

  /* ── Créer une nouvelle structure ── */
  const handleSelectNew = () => {
    const nom = structureSearch.trim();
    setNewStructureNom(nom);
    setStructureId('');
    setIsNewStructure(true);
    setStructureSearch('');
    setShowDropdown(false);
  };

  /* ── Réinitialiser le champ structure ── */
  const clearStructure = () => {
    setStructureId('');
    setNewStructureNom('');
    setIsNewStructure(false);
    setStructureSearch('');
    setShowDropdown(true);
  };

  const structureLabel = isNewStructure
    ? newStructureNom
    : structures.find((s) => s.id === structureId)?.nom ?? '';

  const hasStructure = isNewStructure ? !!newStructureNom : !!structureId;

  /* ── Soumission ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirmPwd) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (!hasStructure) {
      setError('Veuillez sélectionner ou créer votre structure.');
      return;
    }
    setIsLoading(true);
    try {
      await apiService.register({
        prenom, nom, email, password,
        ...(isNewStructure ? { newStructureNom } : { structureId }),
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <BrandPanel subtitle="Créez votre compte Responsable pour accéder à la plateforme" />

      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-12 overflow-y-auto">

        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-lg overflow-hidden border border-gray-100">
            <img src="/armoirie.jpeg" alt="Armoiries du Burkina Faso" className="w-full h-full object-contain p-1" />
          </div>
          <span className="text-xl font-bold text-gray-900">Suivi Démat</span>
          <span className="text-xs text-primary-600 font-semibold tracking-widest uppercase mt-0.5">MTDPCE</span>
        </div>

        <div className="w-full max-w-md">

          {success ? (
            /* ── Succès ── */
            <div className="text-center py-8">
              <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <svg className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Vérifiez votre email !</h2>
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                Un email de vérification a été envoyé à{' '}
                <span className="font-semibold text-gray-900">{email}</span>.
              </p>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Cliquez sur le lien dans l'email pour confirmer votre adresse.
                Le lien est valable <strong>24 heures</strong>.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 mb-8 text-left">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prochaines étapes</p>
                <ol className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                    Vérifiez votre boîte de réception
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                    Cliquez sur le lien de vérification
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                    Les services compétents activeront votre compte
                  </li>
                </ol>
              </div>
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-700 font-semibold hover:underline transition-colors"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-7">
                <h2 className="text-2xl font-bold text-gray-900 leading-tight"> Créer un compte DSI</h2>
                <p className="mt-1.5 text-sm text-gray-500">
                  Remplissez le formulaire — votre compte sera activé par un administrateur.
                </p>
              </div>

              {error && (
                <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl text-sm" role="alert">
                  <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-500" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-4">

                {/* ── Nom ── */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      placeholder="ex : Traoré"
                      className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* ── Prénom ── */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Prénom <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      placeholder="ex : Awa"
                      className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* ── Email ── */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Adresse email <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ex : awa.traore@gov.bf"
                      className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* ── Structure — combobox créable ── */}
                <div ref={dropdownRef} className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Structure <span className="text-red-400">*</span>
                  </label>

                  {/* Affichage si une structure est déjà sélectionnée */}
                  {hasStructure ? (
                    <div className={`flex items-center gap-2.5 px-3 py-2.5 border rounded-xl
                      ${isNewStructure
                        ? 'bg-primary-50 border-primary-300'
                        : 'bg-gray-50 border-gray-300'}`}
                    >
                      <BuildingOfficeIcon className={`h-5 w-5 flex-shrink-0 ${isNewStructure ? 'text-primary-500' : 'text-gray-400'}`} />
                      <span className={`flex-1 text-sm truncate ${isNewStructure ? 'text-primary-800 font-medium' : 'text-gray-900'}`}>
                        {structureLabel}
                      </span>
                      {isNewStructure && (
                        <span className="flex-shrink-0 text-[10px] font-bold bg-primary-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wide">
                          Nouvelle
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={clearStructure}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 text-xs underline transition-colors"
                      >
                        Changer
                      </button>
                    </div>
                  ) : (
                    /* Champ de recherche */
                    <div className="relative">
                      <BuildingOfficeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={structureSearch}
                        onChange={(e) => { setStructureSearch(e.target.value); setShowDropdown(true); }}
                        onFocus={() => setShowDropdown(true)}
                        placeholder="Rechercher ou créer une structure…"
                        autoComplete="off"
                        className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder-gray-400"
                      />
                      <ChevronUpDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  )}

                  {/* Dropdown */}
                  {showDropdown && !hasStructure && (
                    <div className="absolute z-30 mt-1.5 w-full bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
                      {/* En-tête */}
                      {structureSearch.trim().length >= 1 && (
                        <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
                          <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {filteredStructures.length} résultat{filteredStructures.length > 1 ? 's' : ''}
                            {showCreateOption && ' · ou créer ci-dessous'}
                          </span>
                        </div>
                      )}

                      <ul className="max-h-44 overflow-y-auto">
                        {/* Structures existantes */}
                        {filteredStructures.length === 0 && !showCreateOption && (
                          <li className="px-4 py-4 text-sm text-gray-400 text-center">
                            Tapez pour rechercher une structure
                          </li>
                        )}
                        {filteredStructures.map((s) => (
                          <li key={s.id} className="border-b border-gray-50 last:border-0">
                            <button
                              type="button"
                              onClick={() => handleSelectExisting(s)}
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors flex items-center gap-2.5"
                            >
                              {s.sigle && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-600 rounded uppercase">
                                  {s.sigle}
                                </span>
                              )}
                              <span className="truncate text-gray-800">{s.nom}</span>
                            </button>
                          </li>
                        ))}

                        {/* Option créer */}
                        {showCreateOption && (
                          <li className="border-t border-dashed border-primary-200">
                            <button
                              type="button"
                              onClick={handleSelectNew}
                              className="w-full text-left px-4 py-3 text-sm hover:bg-primary-50 transition-colors flex items-center gap-2.5 text-primary-700"
                            >
                              <PlusCircleIcon className="h-5 w-5 flex-shrink-0 text-primary-500" />
                              <span>
                                Créer la structure{' '}
                                <span className="font-semibold">« {structureSearch.trim()} »</span>
                              </span>
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* ── Mot de passe ── */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Mot de passe <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full pl-10 pr-11 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder-gray-400"
                    />
                    <button type="button" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600">
                      {showPwd ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-gray-400">{strength.label}</p>
                    </div>
                  )}
                </div>

                {/* ── Confirmation mot de passe ── */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirmer le mot de passe <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      required
                      value={confirmPwd}
                      onChange={(e) => setConfirmPwd(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full pl-10 pr-11 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder-gray-400"
                    />
                    <button type="button" onClick={() => setShowConfirm((v) => !v)} tabIndex={-1}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600">
                      {showConfirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {confirmPwd.length > 0 && password !== confirmPwd && (
                    <p className="text-xs text-red-500 mt-1.5">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                {/* ── Submit ── */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all mt-2 shadow-sm shadow-primary-200"
                >
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : 'Soumettre la demande'}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500">
                  Vous avez déjà un compte ?{' '}
                  <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                    Se connecter
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        <p className="lg:hidden mt-8 text-center text-xs text-gray-400">© 2026 MTDPCE — Burkina Faso</p>
      </div>
    </div>
  );
};

export default Register;
